import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { Gift, Plus, Search, X, Edit2, Trash2, CheckSquare, Square, ArrowLeft, Eye } from 'lucide-react';
import { memberships, subscribe, addMembership, updateMembership, deleteMembership, catalogServicesOf } from '../../data/store';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

export const MasterPackagesTab = ({ org, showNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [memList, setMemList] = useState([]);
  const [services, setServices] = useState([]);
  
  // View states: 'list', 'create', 'edit', 'view'
  const [viewMode, setViewMode] = useState('list');
  
  const [newPackage, setNewPackage] = useState({ name: '', price: '', sessions: '1', validity: '30', serviceIds: [] });
  const [editPkg, setEditPkg] = useState(null);
  const [viewPkg, setViewPkg] = useState(null);
  const [deletePkgId, setDeletePkgId] = useState(null);

  useEffect(() => {
    const update = () => {
      setMemList(memberships.filter(m => m.orgId === org.id));
      setServices(catalogServicesOf(org.id));
    };
    update();
    return subscribe(update);
  }, [org.id]);

  const handleSave = async () => {
    try {
      await addMembership({
        orgId: org.id,
        name: newPackage.name,
        price: Number(newPackage.price),
        sessions: parseInt(newPackage.sessions) || 1,
        validity: parseInt(newPackage.validity) || 30,
        serviceIds: newPackage.serviceIds || []
      });
      if(showNotification) showNotification('Package created!', 'success');
      setViewMode('list');
      setNewPackage({ name: '', price: '', sessions: '1', validity: '30', serviceIds: [] });
    } catch(err) {
      if(showNotification) showNotification('Failed to create package', 'error');
    }
  };

  const handleEditSave = async () => {
    if(!editPkg) return;
    try {
      await updateMembership(editPkg.id, {
        name: editPkg.name,
        price: Number(editPkg.price),
        sessions: parseInt(editPkg.sessions) || 1,
        validity: parseInt(editPkg.validity) || 30,
        serviceIds: editPkg.serviceIds || []
      });
      if(showNotification) showNotification('Package updated!', 'success');
      setViewMode('list');
      setEditPkg(null);
    } catch(err) {
      if(showNotification) showNotification('Failed to update package', 'error');
    }
  };

  const toggleStatus = async (pkg) => {
    try {
      const current = pkg.status || 'Active';
      const next = current === 'Active' ? 'Inactive' : 'Active';
      await updateMembership(pkg.id, { status: next });
      if(showNotification) showNotification(`Package marked as ${next}`, 'success');
    } catch(err) {
      if(showNotification) showNotification('Status update failed', 'error');
    }
  };

  const confirmDelete = async () => {
    if(!deletePkgId) return;
    try {
      await deleteMembership(deletePkgId);
      if(showNotification) showNotification('Package removed', 'success');
      setDeletePkgId(null);
    } catch(err) {
      if(showNotification) showNotification('Failed to remove', 'error');
    }
  };

  const filtered = memList.filter(p => p.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="flex flex-col gap-4 w-full">
      {viewMode === 'list' && (
        <>
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-md px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
            <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[180px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  placeholder="Search packages by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-md pl-9 pr-4 py-1.5 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setViewMode('create')} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] text-white rounded-md text-xs font-semibold uppercase tracking-[0.14em] transition-colors shadow-sm">
                <Plus size={14} /> Create Package
              </button>
            </div>
          </section>

          <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-[var(--theme-tertiary)] text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    <th className="px-4 py-3 font-semibold">Template Name</th>
                    <th className="px-4 py-3 font-semibold">Price (₹)</th>
                    <th className="px-4 py-3 font-semibold">Total Credits</th>
                    <th className="px-4 py-3 font-semibold">Validity Period</th>
                    <th className="px-4 py-3 font-semibold text-center">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--theme-tertiary)] text-sm">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500 text-sm">
                        No packages found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{pkg.name}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-[var(--theme-primary)]">₹{Number(pkg.price).toFixed(2)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-600">{pkg.sessions}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-600">{pkg.validity} Days</div>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <div className="flex justify-center">
                            <StatusToggle
                              on={pkg.status === 'Active' || !pkg.status}
                              label={pkg.name}
                              onChange={() => toggleStatus(pkg)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <RowActions>
                            <IconAction
                              icon={Eye}
                              label={`View ${pkg.name}`}
                              title="View details"
                              onClick={() => { setViewPkg({ ...pkg, serviceIds: pkg.serviceIds || [] }); setViewMode('view'); }}
                            />
                            <IconAction
                              icon={Edit2}
                              label={`Edit ${pkg.name}`}
                              title="Edit"
                              onClick={() => { setEditPkg({ ...pkg, serviceIds: pkg.serviceIds || [] }); setViewMode('edit'); }}
                            />
                            <IconAction
                              icon={Trash2}
                              tone="danger"
                              label={`Delete ${pkg.name}`}
                              title="Delete"
                              onClick={() => setDeletePkgId(pkg.id)}
                            />
                          </RowActions>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {viewMode === 'create' && (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
          <header className="px-6 py-5 border-b border-[var(--theme-tertiary)] flex items-center gap-4 shrink-0 bg-slate-50">
            <button type="button" onClick={() => setViewMode('list')} className="w-8 h-8 flex items-center justify-center rounded-md bg-white border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors shadow-sm">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 className="text-sm font-extrabold text-[#0d0d0d] tracking-tight">Create a New Package</h2>
              <p className="text-[11px] font-medium text-slate-500 mt-1">Fill in the details below.</p>
            </div>
          </header>
          <div className="p-6 sm:p-10 flex-1">
            <div className="w-full max-w-4xl flex flex-col gap-6">
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Package Name <span className="text-rose-500 ml-1">*</span></span>
                <input type="text" value={newPackage.name} onChange={e=>setNewPackage({...newPackage, name: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="e.g. 10 Signature Haircuts" />
              </label>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <label className="block space-y-1">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Price (₹) <span className="text-rose-500 ml-1">*</span></span>
                  <input type="number" value={newPackage.price} onChange={e=>setNewPackage({...newPackage, price: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="999" />
                </label>
                <label className="block space-y-1">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Total Credits <span className="text-rose-500 ml-1">*</span></span>
                  <input type="number" value={newPackage.sessions} onChange={e=>setNewPackage({...newPackage, sessions: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="10" />
                </label>
                <label className="block space-y-1">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Validity (Days) <span className="text-rose-500 ml-1">*</span></span>
                  <input type="number" value={newPackage.validity} onChange={e=>setNewPackage({...newPackage, validity: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="30" />
                </label>
              </div>
              
              <div className="block space-y-2 mt-4 pt-6 border-t border-slate-100">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Included Services</span>
                <div className="flex flex-col gap-3 overflow-y-visible">
                  {services.length === 0 ? (
                     <p className="text-sm text-slate-400 p-3 font-medium col-span-full bg-white border border-dashed border-slate-200 rounded-xl text-center py-8">No services available.</p>
                  ) : (
                     services.map(s => {
                       const checked = newPackage.serviceIds.includes(s.id);
                       return (
                         <label key={s.id} className={`flex items-start gap-3 p-3 rounded-md border ${checked ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-tint)] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'} cursor-pointer select-none transition-all`}>
                           <div className="pt-0.5">
                             {checked ? <CheckSquare size={18} className="text-[var(--theme-primary)]" /> : <Square size={18} className="text-slate-300" />}
                           </div>
                           <div className="flex items-center justify-between gap-2 min-w-0 flex-1">
                              <span className={`text-[13px] leading-tight truncate ${checked ? 'font-bold text-[#0d0d0d]' : 'font-semibold text-slate-700'}`}>{s.name}</span>
                              <span className="text-[11px] font-medium text-slate-500">₹{(s.price || 0).toFixed(2)}</span>
                           </div>
                           <input type="checkbox" className="hidden" checked={checked} onChange={(e) => {
                             if(e.target.checked) setNewPackage({...newPackage, serviceIds: [...newPackage.serviceIds, s.id]});
                             else setNewPackage({...newPackage, serviceIds: newPackage.serviceIds.filter(id => id !== s.id)});
                           }} />
                         </label>
                       )
                     })
                  )}
                </div>
              </div>
            </div>
          </div>
          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0 bg-slate-50">
            <button type="button" onClick={() => setViewMode('list')} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="button" onClick={handleSave} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] bg-[var(--theme-primary)]">Create Package</button>
          </footer>
        </section>
      )}

      {viewMode === 'edit' && editPkg && (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
          <header className="px-6 py-5 border-b border-[var(--theme-tertiary)] flex items-center gap-4 shrink-0 bg-slate-50">
            <button type="button" onClick={() => setViewMode('list')} className="w-8 h-8 flex items-center justify-center rounded-md bg-white border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors shadow-sm">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 className="text-sm font-extrabold text-[#0d0d0d] tracking-tight">Edit Package</h2>
              <p className="text-[11px] font-medium text-slate-500 mt-1">Update the package details below.</p>
            </div>
          </header>
          <div className="p-6 sm:p-10 flex-1">
            <div className="w-full max-w-4xl flex flex-col gap-6">
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Package Name <span className="text-rose-500 ml-1">*</span></span>
                <input type="text" value={editPkg.name} onChange={e=>setEditPkg({...editPkg, name: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="e.g. 10 Signature Haircuts" />
              </label>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <label className="block space-y-1">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Price (₹) <span className="text-rose-500 ml-1">*</span></span>
                  <input type="number" value={editPkg.price} onChange={e=>setEditPkg({...editPkg, price: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="999" />
                </label>
                <label className="block space-y-1">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Total Credits <span className="text-rose-500 ml-1">*</span></span>
                  <input type="number" value={editPkg.sessions} onChange={e=>setEditPkg({...editPkg, sessions: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="10" />
                </label>
                <label className="block space-y-1">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Validity (Days) <span className="text-rose-500 ml-1">*</span></span>
                  <input type="number" value={editPkg.validity} onChange={e=>setEditPkg({...editPkg, validity: e.target.value})} className="w-full rounded-md bg-slate-50 border border-slate-200 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-all" placeholder="30" />
                </label>
              </div>
              
              <div className="block space-y-2 mt-4 pt-6 border-t border-slate-100">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Included Services</span>
                <div className="flex flex-col gap-3 overflow-y-visible">
                  {services.length === 0 ? (
                     <p className="text-sm text-slate-400 p-3 font-medium col-span-full bg-white border border-dashed border-slate-200 rounded-xl text-center py-8">No services available.</p>
                  ) : (
                     services.map(s => {
                       const checked = editPkg.serviceIds.includes(s.id);
                       return (
                         <label key={s.id} className={`flex items-start gap-3 p-3 rounded-md border ${checked ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-tint)] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'} cursor-pointer select-none transition-all`}>
                           <div className="pt-0.5">
                             {checked ? <CheckSquare size={18} className="text-[var(--theme-primary)]" /> : <Square size={18} className="text-slate-300" />}
                           </div>
                           <div className="flex items-center justify-between gap-2 min-w-0 flex-1">
                              <span className={`text-[13px] leading-tight truncate ${checked ? 'font-bold text-[#0d0d0d]' : 'font-semibold text-slate-700'}`}>{s.name}</span>
                              <span className="text-[11px] font-medium text-slate-500">₹{(s.price || 0).toFixed(2)}</span>
                           </div>
                           <input type="checkbox" className="hidden" checked={checked} onChange={(e) => {
                             if(e.target.checked) setEditPkg({...editPkg, serviceIds: [...editPkg.serviceIds, s.id]});
                             else setEditPkg({...editPkg, serviceIds: editPkg.serviceIds.filter(id => id !== s.id)});
                           }} />
                         </label>
                       )
                     })
                  )}
                </div>
              </div>
            </div>
          </div>
          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0 bg-slate-50">
            <button type="button" onClick={() => setViewMode('list')} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="button" onClick={handleEditSave} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] bg-[var(--theme-primary)]">Save Changes</button>
          </footer>
        </section>
      )}

      {viewMode === 'view' && viewPkg && (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
          <header className="px-6 py-5 border-b border-[var(--theme-tertiary)] flex items-center gap-4 shrink-0 bg-slate-50">
            <button type="button" onClick={() => setViewMode('list')} className="w-8 h-8 flex items-center justify-center rounded-md bg-white border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors shadow-sm">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 className="text-sm font-extrabold text-[#0d0d0d] tracking-tight">Package Details</h2>
              <p className="text-[11px] font-medium text-slate-500 mt-1">Review the package information.</p>
            </div>
          </header>
          <div className="p-6 lg:p-8 flex-1">
            <div className="w-full max-w-4xl flex flex-col gap-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Package Name</p>
                  <p className="font-bold text-slate-800 text-base">{viewPkg.name}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Price</p>
                  <p className="font-bold text-[var(--theme-primary)] text-base">₹{Number(viewPkg.price).toFixed(2)}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Total Sessions</p>
                  <p className="font-bold text-slate-700 text-base">{viewPkg.sessions}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Validity Period</p>
                  <p className="font-bold text-slate-700 text-base">{viewPkg.validity} Days</p>
                </div>
              </div>
              
              <div className="pt-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest">Included Services</h3>
                  <div className="h-px flex-1 bg-slate-200 ml-6"></div>
                </div>
                {(!viewPkg.serviceIds || viewPkg.serviceIds.length === 0) ? (
                  <div className="flex flex-col items-center justify-center p-8 bg-slate-50 border border-slate-200 border-dashed rounded-xl">
                    <p className="text-sm font-medium text-slate-500">No services included.</p>
                    <p className="text-xs text-slate-400 mt-1">Please edit the package to add services.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {viewPkg.serviceIds.map(id => {
                      const srv = services.find(s => s.id === id);
                      return (
                        <div key={id} className="px-4 py-3 bg-white border border-slate-200 rounded-md shadow-sm hover:border-[var(--theme-primary)] hover:shadow-md transition-all flex items-center justify-between group">
                          <span className="text-sm font-bold text-slate-700 group-hover:text-[var(--theme-primary)] transition-colors">{srv ? srv.name : 'Unknown Service'}</span>
                          {srv && <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-md">₹{(srv.price || 0).toFixed(2)}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {deletePkgId && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden"
          >
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}>
                <Trash2 size={20} />
              </div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove this package?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">This action cannot be undone.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setDeletePkgId(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="button" onClick={confirmDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600 transition-colors">Remove</button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

    </div>
  );
};

MasterPackagesTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
